import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { requestSampleStart } from "@/lib/sampleData";
import { logEvent } from "@/lib/analytics";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { QuickAddAccountDialog } from "@/components/QuickAddAccountDialog";
import {
  Sparkle, ChartLineUp, UploadSimple, Gauge, ArrowRight, PlusCircle, FolderOpen, PlayCircle,
} from "@phosphor-icons/react";

const JK = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

// Optional 30-second product tour — kept from the original onboarding, no longer a mandatory gate.
const TOUR = [
  { icon: ChartLineUp, title: "What RecoverOS does", desc: "RecoverOS turns your receivables spreadsheet into a live collections command center — track outstanding, follow-ups, settlements and recoveries in one place, for any industry." },
  { icon: UploadSimple, title: "What happens after you upload", desc: "Upload your Excel or CSV once. RecoverOS auto-maps your columns and builds your portfolio in minutes — your original file stays exactly as it is." },
  { icon: Gauge, title: "The insights you'll receive", desc: "Instantly see your Money Stuck Analyzer™, Recovery Health Score, priority accounts, settlement opportunities and an executive dashboard." },
];

export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("choose"); // "choose" | "tour"
  const [tourStep, setTourStep] = useState(0);
  const [quickOpen, setQuickOpen] = useState(false);
  const first = (user?.name || "there").split(" ")[0];
  const canSample = ["client_admin", "manager"].includes(user?.role);

  useEffect(() => { logEvent("welcome_viewed", {}); }, []);

  const markOnboarded = () => {
    const uid = user?.user_id || user?.email;
    if (uid) localStorage.setItem(`recoveros_onboarded_${uid}`, "1");
  };

  const choose = (action) => {
    logEvent("welcome_action_selected", { action });
    if (action !== "optional_tour") logEvent("welcome_action_chosen", { choice: action });
    if (action === "upload_excel") { markOnboarded(); navigate("/import"); }
    else if (action === "add_one_account") { setQuickOpen(true); }
    else if (action === "sample_portfolio") { markOnboarded(); requestSampleStart(navigate); }
    else if (action === "optional_tour") { setTourStep(0); setView("tour"); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="onboarding-page">
      <header className="flex items-center justify-between px-6 lg:px-10 h-16 border-b border-slate-200 bg-white">
        <Brand iconSize={30} textClass="text-lg" style={JK} />
        <button onClick={() => { markOnboarded(); navigate("/dashboard"); }} className="text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="onboarding-skip">Skip for now</button>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        {view === "choose" ? (
          <div className="w-full max-w-2xl" data-testid="onboarding-choose">
            <div className="text-center">
              <div className="h-14 w-14 rounded-full bg-[#0B3D91]/10 flex items-center justify-center mx-auto">
                <Sparkle size={28} weight="fill" className="text-[#0B3D91]" />
              </div>
              <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold text-slate-950" style={JK}>Welcome to RecoverOS, {first}!</h1>
              <p className="mt-2 text-slate-600 max-w-lg mx-auto">See where your money is stuck — and what needs attention first. Pick how you'd like to start.</p>
            </div>

            <div className="mt-8 space-y-3">
              {/* PRIMARY — Upload Excel */}
              <button
                onClick={() => choose("upload_excel")}
                data-testid="welcome-upload-excel"
                className="group w-full flex items-center gap-4 text-left rounded-sm border-2 border-[#16A34A] bg-[#16A34A] text-white p-5 hover:-translate-y-0.5 transition-transform"
              >
                <div className="h-11 w-11 rounded-sm bg-white/15 flex items-center justify-center shrink-0"><UploadSimple size={24} weight="bold" /></div>
                <div className="flex-1">
                  <div className="text-lg font-bold" style={JK}>Upload your Excel</div>
                  <div className="text-sm text-white/85">Analyse your actual receivables portfolio and identify what needs attention.</div>
                </div>
                <ArrowRight size={20} weight="bold" className="shrink-0 opacity-80 group-hover:translate-x-0.5 transition-transform" />
              </button>

              {/* SECONDARY — Add one account */}
              <button
                onClick={() => choose("add_one_account")}
                data-testid="welcome-add-one-account"
                className="group w-full flex items-center gap-4 text-left rounded-sm border-2 border-[#0B3D91] bg-white text-slate-900 p-5 hover:bg-[#0B3D91]/[0.04] hover:-translate-y-0.5 transition-[transform,background-color]"
              >
                <div className="h-11 w-11 rounded-sm bg-[#0B3D91]/10 flex items-center justify-center shrink-0"><PlusCircle size={24} weight="bold" className="text-[#0B3D91]" /></div>
                <div className="flex-1">
                  <div className="text-lg font-bold text-[#0B3D91]" style={JK}>Add one account</div>
                  <div className="text-sm text-slate-500">Want to try RecoverOS with one real customer first? Add one and see how the workflow works.</div>
                </div>
                <ArrowRight size={20} weight="bold" className="shrink-0 text-[#0B3D91] group-hover:translate-x-0.5 transition-transform" />
              </button>

              {/* TERTIARY — Explore sample */}
              {canSample && (
                <button
                  onClick={() => choose("sample_portfolio")}
                  data-testid="welcome-explore-sample"
                  className="group w-full flex items-center gap-4 text-left rounded-sm border border-slate-200 bg-white text-slate-900 p-5 hover:border-slate-300 hover:-translate-y-0.5 transition-[transform,border-color]"
                >
                  <div className="h-11 w-11 rounded-sm bg-slate-100 flex items-center justify-center shrink-0"><FolderOpen size={24} weight="bold" className="text-slate-600" /></div>
                  <div className="flex-1">
                    <div className="text-lg font-bold text-slate-800" style={JK}>Explore sample portfolio</div>
                    <div className="text-sm text-slate-500">Not ready with your data? Explore RecoverOS using 50 sample receivables.</div>
                  </div>
                  <ArrowRight size={20} weight="bold" className="shrink-0 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                </button>
              )}
            </div>

            {/* OPTIONAL — 30-second tour */}
            <div className="mt-6 text-center">
              <button onClick={() => choose("optional_tour")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="welcome-optional-tour">
                <PlayCircle size={16} weight="bold" /> Take a 30-second tour first
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-xl" data-testid="onboarding-tour">
            {(() => {
              const t = TOUR[tourStep]; const Icon = t.icon;
              const last = tourStep === TOUR.length - 1;
              return (
                <div className="text-center">
                  <div className="text-xs font-bold uppercase tracking-widest text-[#0B3D91]">Step {tourStep + 1} of {TOUR.length}</div>
                  <div className="mt-5 h-16 w-16 rounded-sm bg-[#16A34A]/10 flex items-center justify-center mx-auto"><Icon size={30} weight="bold" className="text-[#16A34A]" /></div>
                  <h2 className="mt-5 text-2xl font-extrabold text-slate-950" style={JK}>{t.title}</h2>
                  <p className="mt-3 text-slate-600 max-w-md mx-auto">{t.desc}</p>
                  <div className="mt-8 flex items-center justify-center gap-3">
                    <Button variant="outline" onClick={() => (tourStep === 0 ? setView("choose") : setTourStep((s) => s - 1))} className="rounded-sm h-11 px-5" data-testid="tour-back">
                      {tourStep === 0 ? "Back to options" : "Back"}
                    </Button>
                    {last ? (
                      <Button onClick={() => setView("choose")} className="rounded-sm gap-1.5 bg-[#0B3D91] hover:bg-[#0a3578] h-11 px-6" data-testid="tour-finish">
                        Choose how to start <ArrowRight size={16} weight="bold" />
                      </Button>
                    ) : (
                      <Button onClick={() => setTourStep((s) => s + 1)} className="rounded-sm gap-1.5 bg-[#0B3D91] hover:bg-[#0a3578] h-11 px-6" data-testid="tour-next">
                        Next <ArrowRight size={16} weight="bold" />
                      </Button>
                    )}
                  </div>
                  <div className="mt-6 flex items-center justify-center gap-1.5">
                    {TOUR.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === tourStep ? "w-5 bg-[#0B3D91]" : "w-1.5 bg-slate-300"}`} />)}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </main>

      <QuickAddAccountDialog open={quickOpen} onOpenChange={setQuickOpen} source="welcome" />
    </div>
  );
}
